import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Joyride, EVENTS, STATUS, type EventData, type Step, type TooltipRenderProps } from 'react-joyride';
import { X } from 'lucide-react';
import { useLocation } from 'react-router';
import { useI18n } from '../i18n/I18nProvider';
import { useHelp } from './HelpContext';
import { matchRoute } from './routeMatch';
import { getTour } from './tours';
import { resolveTourSteps, type ResolvedStep } from './tourSteps';
import './help.css';

/** How long to wait for a page (often a lazily loaded one) to draw the tour's targets. */
const WAIT_MS = 5000;
const POLL_MS = 150;

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && !!window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
}

/** Our own tooltip: the Kudos tokens and copy, Joyride only places it. */
function TourTooltip({ index, size, step, isLastStep, backProps, primaryProps, skipProps, tooltipProps }: TooltipRenderProps) {
  const { t } = useI18n();
  return (
    <div className="khelp-tip" {...tooltipProps} aria-labelledby="khelp-tip-title" aria-describedby="khelp-tip-body">
      <div className="khelp-tip__head">
        <span className="khelp-tip__count">{t('help.tour.progress', { current: index + 1, total: size })}</span>
        <button type="button" className="khelp-icon-btn khelp-icon-btn--small" {...skipProps} aria-label={t('help.tour.end')} title={t('help.tour.end')}>
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      <p id="khelp-tip-title" className="khelp-tip__title">{step.title}</p>
      <p id="khelp-tip-body" className="khelp-tip__body">{step.content}</p>
      <div className="khelp-tip__foot">
        <span className="khelp-tip__dots" aria-hidden="true">
          {Array.from({ length: size }, (_, i) => <span key={i} className={`khelp-tip__dot${i === index ? ' khelp-tip__dot--on' : ''}`} />)}
        </span>
        {index > 0 && (
          <button type="button" className="khelp-btn khelp-btn--quiet" {...backProps} aria-label={t('help.tour.back')} title={t('help.tour.back')}>
            {t('help.tour.back')}
          </button>
        )}
        <button type="button" className="khelp-btn khelp-btn--primary" {...primaryProps}
          aria-label={isLastStep ? t('help.tour.done') : t('help.tour.next')} title={isLastStep ? t('help.tour.done') : t('help.tour.next')}>
          {isLastStep ? t('help.tour.done') : t('help.tour.next')}
        </button>
      </div>
    </div>
  );
}

/**
 * Runs one tour: goes nowhere by itself (HelpContext has already navigated to the
 * tour's page), waits for the page to draw, leaves out steps whose target is not
 * there, and records "done" or "skipped" for this person at the end. Escape ends it.
 */
export default function TourRunner({ tourId }: { tourId: string }) {
  const { t } = useI18n();
  const help = useHelp()!;
  const location = useLocation();
  const tour = getTour(tourId);
  const [resolved, setResolved] = useState<ResolvedStep[] | null>(null);
  const finishedRef = useRef(false);
  const reduce = reducedMotion();
  const { finishTour } = help.actions;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // Wait until the tour's page is showing and at least one target is on it.
  useEffect(() => {
    if (!tour || resolved) return undefined;
    const started = Date.now();
    let timer = 0;
    const attempt = () => {
      const onPage = matchRoute(tour.route, pathRef.current);
      const steps = onPage ? resolveTourSteps(tour, { log: () => {} }) : [];
      // Give a page that is still loading the full wait before settling for fewer steps.
      if (onPage && (steps.length === tour.steps.length || (steps.length > 0 && Date.now() - started > 1200))) {
        setResolved(resolveTourSteps(tour));
        return;
      }
      if (Date.now() - started > WAIT_MS) {
        // eslint-disable-next-line no-console
        console.info(`[help] tour "${tour.id}" not started: none of its targets appeared on ${pathRef.current}`);
        finishedRef.current = true;
        setResolved([]);
        return;
      }
      timer = window.setTimeout(attempt, POLL_MS);
    };
    timer = window.setTimeout(attempt, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [tour, resolved]);

  // Nothing to show: hand back without recording anything.
  const { cancelTour } = help.actions;
  useEffect(() => {
    if (resolved && resolved.length === 0) cancelTour();
  }, [resolved, cancelTour]);

  // Leaving the tour's page ends the tour.
  useEffect(() => {
    if (!tour || !resolved || resolved.length === 0 || finishedRef.current) return;
    if (!matchRoute(tour.route, location.pathname)) {
      finishedRef.current = true;
      finishTour(tour.id, 'skipped');
    }
  }, [location.pathname, tour, resolved, finishTour]);

  const steps: Step[] = useMemo(() => (resolved || []).map((s) => ({
    target: s.element,
    title: t(`help.tour.${tourId}.${s.key}.title`),
    content: t(`help.tour.${tourId}.${s.key}.body`),
    placement: 'auto',
    spotlightPadding: s.padding ?? 6,
    // A tab in a row that scrolls sideways is brought into view first.
    before: async () => {
      if (typeof s.element.scrollIntoView === 'function') {
        s.element.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduce ? 'auto' : 'smooth' });
      }
      if (!reduce) await new Promise((r) => setTimeout(r, 200));
    },
  })), [resolved, t, tourId, reduce]);

  useEffect(() => {
    if (!resolved || resolved.length === 0) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !finishedRef.current) {
        e.preventDefault();
        finishedRef.current = true;
        finishTour(tourId, 'skipped');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [resolved, finishTour, tourId]);

  if (!tour || !resolved || resolved.length === 0) return null;

  const onEvent = (data: EventData, controls: any) => {
    if (data.type === EVENTS.TARGET_NOT_FOUND) {
      // eslint-disable-next-line no-console
      console.info(`[help] tour "${tourId}": a step's target went away; moving on`);
      controls?.next?.();
      return;
    }
    if (data.type === EVENTS.TOUR_END || data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      if (finishedRef.current) return;
      finishedRef.current = true;
      finishTour(tourId, data.status === STATUS.SKIPPED ? 'skipped' : 'done');
    }
  };

  return (
    <Joyride
      run
      continuous
      steps={steps}
      onEvent={onEvent}
      tooltipComponent={TourTooltip}
      scrollToFirstStep
      options={{
        zIndex: 1350,
        primaryColor: '#7F56FA',
        overlayColor: 'rgba(16, 24, 39, 0.48)',
        backgroundColor: '#ffffff',
        textColor: '#101827',
        arrowColor: '#ffffff',
        spotlightRadius: 10,
        spotlightPadding: 6,
        skipBeacon: true,
        skipScroll: false,
        scrollDuration: reduce ? 0 : 300,
        scrollOffset: 96,
        targetWaitTimeout: 0,
        overlayClickAction: false,
        dismissKeyAction: false,
        closeButtonAction: 'skip',
        buttons: ['back', 'primary', 'skip'],
        width: 340,
      }}
      styles={reduce ? { overlay: { transition: 'none' }, floater: { transition: 'none' } } : undefined}
      locale={{ back: t('help.tour.back'), next: t('help.tour.next'), last: t('help.tour.done'), skip: t('help.tour.end'), close: t('help.tour.end') }}
    />
  );
}
